import { useCallback, useEffect, useRef, useState } from "react";
import "@/theme.css";
import { Headphones } from "lucide-react";
import { api } from "@/lib/api";
import { fireAlarm, unlockAudio } from "@/lib/alarm";
import { pushSignal, requestNotify } from "@/lib/notify";
import { TokenBanner } from "@/components/TokenBanner";
import { TopBar } from "@/components/TopBar";
import { StockView } from "@/components/StockView";
import { ConnectCard } from "@/components/ConnectCard";
import { useLiveTicks } from "@/lib/useLiveTicks";
import { AlertBanner } from "@/components/AlertBanner";
import { AlertCenter } from "@/components/AlertCenter";
import { ScannerTable } from "@/components/ScannerTable";
import { GrowwConnectModal } from "@/components/GrowwConnectModal";
import { StrategyModal } from "@/components/StrategyModal";

const App = () => {
  const [scanner, setScanner] = useState({ items: [], updated: null });
  const [symbol, setSymbol] = useState("RELIANCE");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState({ connected: false, token_state: "loading" });
  const [dataError, setDataError] = useState("");
  const userPicked = useRef(false);
  const live = status.token_state === "ok";
  const [orders, setOrders] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [banner, setBanner] = useState(null);
  const [prefs, setPrefs] = useState({ tone: true, voice: true, push: typeof Notification !== "undefined" && Notification.permission === "granted" });
  const [installEvt, setInstallEvt] = useState(null);
  const [modal, setModal] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [toast, setToast] = useState("");
  const seen = useRef(new Set());
  const prefsRef = useRef(prefs); prefsRef.current = prefs;

  const toastTimer = useRef();
  const notify = msg => { setToast(msg); clearTimeout(toastTimer.current); toastTimer.current = setTimeout(() => setToast(""), 3500); };

  const ring = useCallback((signal, symbol) => {
    const alert = { ...signal, symbol };
    setAlerts(a => [alert, ...a].slice(0, 30)); setBanner(alert);
    fireAlarm(signal, symbol, prefsRef.current);
    if (prefsRef.current.push) pushSignal(signal, symbol);
  }, []);

  const load = useCallback(async sym => {
    const data = await api.dashboard(sym);
    const symbol = data.symbol;
    const fresh = data.signals.filter(s => !seen.current.has(s.id));
    const firstLoad = !seen.current.has(`loaded-${symbol}`);
    fresh.forEach(s => seen.current.add(s.id)); seen.current.add(`loaded-${symbol}`);
    const latest = fresh[fresh.length - 1];
    if (!firstLoad && latest && latest.index >= data.candles.length - 2) ring(latest, symbol);
    setData(data); setDataError("");
  }, [ring]);

  const loadScanner = useCallback(async () => {
    const data = await api.scanner(); setScanner(data);
    if (!userPicked.current) setSymbol(data.items[0].symbol);
  }, []);

  const onDataError = useCallback(e => { setDataError(e.message); if (/GROWW_NOT_CONNECTED|invalid or expired|rejected/.test(e.message)) api.growwStatus().then(setStatus); }, []);

  useEffect(() => {
    api.growwStatus().then(setStatus); api.orders().then(setOrders); api.strategy().then(setStrategy);
    const id = setInterval(() => api.growwStatus().then(setStatus), 60000); return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!live) return undefined;
    const run = () => loadScanner().catch(onDataError);
    run(); const id = setInterval(run, 180000); return () => clearInterval(id);
  }, [live, loadScanner, onDataError]);

  useEffect(() => {
    if (!live || !symbol) return undefined;
    const refresh = () => load(symbol).catch(onDataError);
    refresh(); const id = setInterval(refresh, 15000); return () => clearInterval(id);
  }, [live, symbol, load, onDataError]);

  useEffect(() => { const unlock = () => unlockAudio(); window.addEventListener("pointerdown", unlock); return () => window.removeEventListener("pointerdown", unlock); }, []);

  useEffect(() => { const esc = e => e.key === "Escape" && setModal(null); window.addEventListener("keydown", esc); return () => window.removeEventListener("keydown", esc); }, []);

  useEffect(() => { const h = e => { e.preventDefault(); setInstallEvt(e); }; window.addEventListener("beforeinstallprompt", h); return () => window.removeEventListener("beforeinstallprompt", h); }, []);

  const install = async () => { installEvt.prompt(); await installEvt.userChoice; setInstallEvt(null); };
  const togglePush = async () => {
    if (prefs.push) { setPrefs({ ...prefs, push: false }); return; }
    const ok = await requestNotify();
    setPrefs({ ...prefs, push: ok }); notify(ok ? "Phone notifications enabled" : "Notifications blocked — allow them in browser settings");
  };
  const growwActions = {
    onConnect: async t => setStatus(await api.connect(t)),
    onConnectTotp: async (t, s) => setStatus(await api.connectTotp(t, s)),
    onRefresh: async () => { const st = await api.refreshToken(); setStatus(st); if (st.token_state === "expired") throw new Error(st.token_error || "Refresh failed"); notify("Groww token renewed"); },
    onDisconnect: async () => setStatus(await api.disconnect()),
  };

  const tick = useLiveTicks(live ? symbol : null);
  const view = sym => { userPicked.current = true; setSymbol(sym); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const order = async body => {
    try { const o = await api.placeOrder(body); setOrders(list => [o, ...list].slice(0, 20)); notify(`Opened Groww for ${o.side} ${o.quantity} ${o.symbol} @ ₹${o.price} — complete the order there`); }
    catch (e) { notify(e.message); }
  };

  const testAlarm = () => { unlockAudio(); const d = data; const s = d?.latest_signal || { type: "BUY", price: d?.quote.price || 0, time: "--:--", strength: 0, reason: "Test alarm" }; ring({ ...s, id: `test-${Date.now()}` }, d?.symbol || "TEST"); };

  return <main className="terminal" data-testid="trading-terminal">
    <TopBar status={status} onPick={view} onInstall={installEvt ? install : null} onOpenGroww={() => setModal("groww")} onOpenStrategy={() => setModal("strategy")} />
    <TokenBanner status={status} onUpdate={growwActions.onConnect} onRefresh={growwActions.onRefresh} />
    <AlertBanner alert={banner} onDismiss={() => setBanner(null)} onFocus={sym => { view(sym); setBanner(null); }} />
    <div className="desk">
      {live || status.token_state === "loading" ? <StockView data={data?.symbol === symbol ? data : null} error={live ? dataError : ""} tick={tick} onOrder={order} />
        : <ConnectCard status={status} error={dataError.startsWith("GROWW_NOT_CONNECTED") ? "" : dataError} onConnect={growwActions.onConnect} onConnectTotp={growwActions.onConnectTotp} />}
      <AlertCenter prefs={prefs} setPrefs={setPrefs} onTogglePush={togglePush} alerts={alerts} orders={orders} onTest={testAlarm} />
    </div>
    {live && scanner.items.length ? <ScannerTable items={scanner.items} current={symbol} onView={view} updated={scanner.updated} />
      : <section className="scanner"><div className="scanner-wait" data-testid="scanner-wait">{live ? "Ranking the Top 50 with live Groww candles…" : "Live Top 50 ranking appears after Groww is connected"}</div></section>}
    {toast && <div className="toast" data-testid="order-toast"><Headphones size={15} />{toast}</div>}
    {modal === "groww" && <GrowwConnectModal status={status} onClose={() => setModal(null)} {...growwActions} />}
    {modal === "strategy" && strategy && <StrategyModal initial={strategy} onClose={() => setModal(null)} onSave={async s => { const r = await api.saveStrategy(s); setStrategy(r.settings); notify("Strategy saved"); if (live) { loadScanner().catch(onDataError); load(symbol).catch(onDataError); } }} />}
  </main>;
};

export default App;
